import { Theme, createTheme } from "@mui/material";

export type ThemeMode = "light" | "dark";

// Solid chrome color shared by the AppBar, the drawer header, and the iOS
// status-bar fill, one shade per mode — all three must always match so the
// header reads as one continuous surface (see AGENTS.md).
export const HEADER_COLORS: Record<ThemeMode, string> = {
  light: "#4a3526",
  dark: "#241a12",
};

export const HEADER_TEXT_COLOR = "#f5ecd8";

// The page background per mode. Must stay a single solid color applied to
// html/body: iOS samples the body background for rubber-band overscroll and
// the safe areas, and only honors a solid color there (see AGENTS.md).
export const PAGE_BACKGROUND_COLORS: Record<ThemeMode, string> = {
  light: "#f1e7d3", // parchment
  dark: "#1a130d", // deep espresso
};

export function buildTheme(mode: ThemeMode): Theme {
  const light = mode === "light";

  return createTheme({
    palette: light
      ? {
          mode,
          // Warm guitar palette drawn from the fretboard: stained walnut,
          // brass, and bone/parchment.
          primary: {
            main: "#6e4724", // walnut
            light: "#9c6b3f",
            dark: "#472c15",
            contrastText: "#faf3e6",
          },
          secondary: {
            main: "#c0883c", // brass
          },
          background: {
            default: PAGE_BACKGROUND_COLORS.light,
            paper: "#fbf6ec", // warm bone
          },
          text: {
            primary: "#2e2114", // espresso
            secondary: "#6b5640", // walnut-grey
          },
        }
      : {
          mode,
          // The same wood-and-brass family flipped for a dark room: brass
          // carries the accents on deep espresso surfaces.
          primary: {
            main: "#c99a5b", // brass-tan
            light: "#ddb87e",
            dark: "#a87b3f",
            contrastText: "#241a12",
          },
          secondary: {
            main: "#c0883c", // brass
          },
          background: {
            default: PAGE_BACKGROUND_COLORS.dark,
            paper: "#261c13",
          },
          text: {
            primary: "#f0e7d6",
            secondary: "#bda88c",
          },
          divider: "rgba(240, 231, 214, 0.14)",
        },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily:
        '"Inter", "Segoe UI", system-ui, -apple-system, "Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "html, body": {
            backgroundColor: PAGE_BACKGROUND_COLORS[mode],
          },
          body: {
            minHeight: "100vh",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 10,
            transition:
              "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 120ms ease",
            "&:active": {
              transform: "translateY(1px)",
            },
          },
          outlined: light
            ? {
                borderColor: "rgba(110, 71, 36, 0.5)",
                color: "#6e4724",
                backgroundColor: "rgba(255, 252, 245, 0.6)",
                "&:hover": {
                  borderColor: "#6e4724",
                  backgroundColor: "rgba(110, 71, 36, 0.08)",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(0, 0, 0, 0.12)",
                },
              }
            : {
                borderColor: "rgba(201, 154, 91, 0.5)",
                color: "#ddb87e",
                backgroundColor: "rgba(201, 154, 91, 0.06)",
                "&:hover": {
                  borderColor: "#c99a5b",
                  backgroundColor: "rgba(201, 154, 91, 0.14)",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(255, 255, 255, 0.12)",
                },
              },
          contained: light
            ? {
                boxShadow: "0 6px 16px rgba(74, 47, 21, 0.28)",
                "&:hover": {
                  boxShadow: "0 8px 22px rgba(74, 47, 21, 0.36)",
                },
              }
            : {
                boxShadow: "0 6px 16px rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  boxShadow: "0 8px 22px rgba(0, 0, 0, 0.6)",
                },
              },
        },
      },
      MuiTypography: {
        styleOverrides: {
          // Headings and body share the same left padding so titles and the text
          // beneath them align by default; headings also get consistent vertical
          // breathing room and weight.
          h3: {
            marginTop: "0.35em",
            marginBottom: "0.35em",
            paddingLeft: "15px",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          },
          h4: {
            marginTop: "0.35em",
            marginBottom: "0.35em",
            paddingLeft: "15px",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          },
          body1: {
            paddingLeft: "15px",
          },
        },
      },
    },
  });
}
